/**
 * Simple Suggestions Service
 *
 * Directly imports and uses TanStack Query composables.
 * No complex orchestration - just simple filtering.
 */

import { computed, ref } from 'vue'

import { useChannels } from '@communities/channels-layer/composables/useChannels'
import type { IChannel } from '@communities/channels-layer/types/channels'
import { useEvents } from '@communities/events-layer/composables/useEvents'
import type { EventMentionData } from '@communities/events-layer/types/events'
import { useGroupInitialization } from '@communities/groups-layer/composables/useGroupInitialization'
import { useLearning } from '@communities/learning-layer/composables/useLearning'
import type { ICourseProduct } from '@communities/learning-layer/types/learning'

/**
 * Simple composable that provides filtered suggestions
 * Directly uses TanStack Query composables - no parameter passing needed!
 */
export const useSuggestions = () => {
  // Get current group context
  const { currentGroup } = useGroupInitialization(undefined, 'useSuggestions')
  const groupId = computed(() => currentGroup.value?._id)
  const locationId = computed(() => currentGroup.value?.locationId)

  // Get data via TanStack Query
  const channels = useChannels(groupId)
  const events = useEvents(groupId)
  const learning = useLearning(groupId, locationId)

  // Create events list query using the new factory function API
  const eventsListQuery = events.createEventsListQuery()

  // Reactive search query for courses
  const coursesSearchQuery = ref('')
  const coursesQuery = learning.getCoursesSuggestions(coursesSearchQuery)

  // Simple filtering functions
  const filterChannels = (query: string = ''): IChannel[] => {
    const channelsData = channels.getChannelsList.data.value || []

    if (!query.trim()) {
      return channelsData
    }

    return channelsData.filter(channel => channel.name.toLowerCase().includes(query.toLowerCase()))
  }

  const filterEvents = (query: string = ''): EventMentionData[] => {
    const eventsData = eventsListQuery.data.value || []

    if (!query.trim()) {
      return eventsData
    }

    return eventsData.filter(event => event.label.toLowerCase().includes(query.toLowerCase()))
  }

  const filterCourses = (query: string = ''): ICourseProduct[] => {
    // Update search query for the reactive query
    coursesSearchQuery.value = query

    // Return courses data from the query
    return coursesQuery.data.value || []
  }

  // Combined filtering
  const filterAll = (query: string = '') => {
    return {
      channels: filterChannels(query),
      events: filterEvents(query),
      courses: filterCourses(query),
    }
  }

  // Loading states
  const isLoading = computed(() => {
    return channels.getChannelsList.isLoading.value || eventsListQuery.isLoading.value || coursesQuery.isLoading.value
  })

  return {
    // Data
    channels: computed(() => channels.getChannelsList.data.value || []),
    events: computed(() => eventsListQuery.data.value || []),
    courses: computed(() => coursesQuery.data.value || []),

    // Filtering functions
    filterChannels,
    filterEvents,
    filterCourses,
    filterAll,

    // States
    isLoading,
  }
}

// Legacy export for backward compatibility (will be removed)
export class SuggestionsService {
  filterChannels(channels: IChannel[], query: string = ''): IChannel[] {
    if (!query.trim()) return channels
    return channels.filter(channel => channel.name.toLowerCase().includes(query.toLowerCase()))
  }

  filterEvents(events: EventMentionData[], query: string = ''): EventMentionData[] {
    if (!query.trim()) return events
    return events.filter(event => event.label.toLowerCase().includes(query.toLowerCase()))
  }
}

export const useSuggestionsService = () => new SuggestionsService()
